import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Cable, ShieldCheck, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function LoginPage() {
  const { user, login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate("/");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto grid min-h-screen max-w-6xl grid-cols-1 lg:grid-cols-2">
        {/* left decorative panel */}
        <div className="relative hidden flex-col justify-between border-r border-border bg-card p-10 lg:flex">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Cable className="h-4 w-4" />
            </div>
            <div className="leading-tight">
              <div className="font-heading text-sm font-bold">SSO Bridge</div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Halo · cPanel</div>
            </div>
          </div>

          <div className="max-w-md">
            <div className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              <ShieldCheck className="h-3 w-3" /> Admin console
            </div>
            <h1 className="font-heading text-3xl font-bold leading-[1.1] tracking-tight text-foreground">
              Zero-touch handoff from your Halo portal to cPanel.
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Manage WHM servers, per-client mappings, and every SSO handoff
              from a single dense admin surface. Encrypted secrets, signed
              redirects, full audit trail.
            </p>
            <ul className="mt-6 space-y-2 text-sm">
              {[
                "Multi-WHM support with per-client routing",
                "Signed state params + IP allowlist + rate limits",
                "Shadow-login for helpdesk from the Mappings table",
                "SMTP2Go alerts on failed SSO attempts",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2 text-muted-foreground">
                  <ArrowRight className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="text-xs text-muted-foreground">
            <span className="font-mono">v1.0</span> · admin only · JWT
          </div>
        </div>

        {/* login form */}
        <div className="flex items-center justify-center p-6 sm:p-10">
          <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
            <div>
              <h2 className="font-heading text-2xl font-bold tracking-tight">Sign in</h2>
              <p className="mt-1 text-sm text-muted-foreground">Enter your admin credentials to continue.</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" name="email" type="email" autoComplete="username"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password" name="password" type="password" autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required
                data-testid="login-password-input"
              />
            </div>

            {error ? (
              <div
                className="rounded-md border border-red-500/30 bg-red-500/5 px-3 py-2 text-sm text-red-600 dark:text-red-400"
                data-testid="login-error"
              >
                {error}
              </div>
            ) : null}

            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
              {loading ? "Signing in…" : "Sign in"}
            </Button>

            <p className="text-center text-xs text-muted-foreground">
              Contact your MSP admin if you need access.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
